<template>
  <!-- Bottom right, and a card rather than a modal.
       A modal blocks the first screen of the day over something optional. But this is the one
       moment a user is told the Suite has been redesigned, so it is sized and weighted as an
       announcement rather than as a routine notice: the illustration carries the brand, the
       headline is a headline, and the primary action is the widest thing in the card.
       `pointer-events-none` on the positioner keeps the corner of the page clickable; the card
       itself takes them back. -->
  <Transition
    appear
    enter-active-class="transition duration-500 ease-out"
    enter-from-class="translate-y-4 opacity-0 sm:translate-y-6"
    enter-to-class="translate-y-0 opacity-100"
    leave-active-class="transition duration-150 ease-in"
    leave-from-class="opacity-100"
    leave-to-class="opacity-0"
  >
    <div
      v-if="visible"
      class="pointer-events-none fixed inset-x-4 bottom-4 z-40 flex justify-end sm:inset-x-auto sm:right-6 sm:bottom-6"
      data-test="design-system-invitation"
    >
      <div
        role="region"
        :aria-label="$t(copy.title)"
        class="pointer-events-auto relative w-full overflow-hidden rounded-lg bg-white p-5 shadow-[0_12px_40px_0_rgba(63,30,89,0.22)] ring-1 ring-black/5 sm:w-[420px]"
      >
        <button
          type="button"
          class="absolute top-3 right-3 flex size-7 cursor-pointer items-center justify-center rounded text-fg-mauve transition-colors duration-150 hover:bg-surface-violet hover:text-primary"
          :aria-label="$t('appearance.invitation.later')"
          data-test="design-system-invitation-close"
          @click="snooze"
        >
          <FontAwesomeIcon :icon="['fal', 'times']" class="text-base" />
        </button>

        <div class="flex items-start gap-4">
          <!-- The design system's own palette illustration, on its brand tint. Says "this is about
               how the Suite looks" before a word is read, which no amount of copy does as fast. -->
          <span
            class="flex size-14 shrink-0 items-center justify-center rounded-full bg-surface-violet"
            aria-hidden="true"
          >
            <img
              :src="Vapor.asset('images/ds-illustrations/palette.svg')"
              alt=""
              class="size-8"
            />
          </span>

          <div class="min-w-0 pr-6">
            <h3
              class="text-lg leading-tight font-extrabold text-fg"
              v-text="$t(copy.title)"
            />
            <p
              class="mt-1.5 text-sm leading-snug text-fg-mauve"
              v-text="$t(copy.body)"
            />
          </div>
        </div>

        <div class="mt-5 flex flex-wrap items-center gap-2">
          <!-- Invitation: try it, or be left alone for a while. -->
          <template v-if="!justSwitched">
            <button
              type="button"
              class="flex-1 rounded-[5px] bg-primary px-5 py-2.5 text-sm font-bold text-white transition-colors duration-150 hover:bg-primary-dark disabled:opacity-60"
              :disabled="submitting"
              data-test="design-system-invitation-accept"
              @click="activate"
            >
              {{ $t('appearance.invitation.activate') }}
            </button>
            <button
              type="button"
              class="rounded-[5px] px-4 py-2.5 text-sm font-bold text-fg-mauve transition-colors duration-150 hover:bg-surface-violet hover:text-primary disabled:opacity-60"
              :disabled="submitting"
              data-test="design-system-invitation-later"
              @click="snooze"
            >
              {{ $t('appearance.invitation.later') }}
            </button>
          </template>

          <!-- Just switched: the way back, as a button. Both looks put the user menu top right,
               but in different shells and at different places on the screen - the classic header
               spans the page, the design system's headline row starts after the sidebar - so an
               instruction naming it describes a shell they are seeing for the first time, exactly
               when they are least oriented. -->
          <button
            v-else
            type="button"
            class="flex-1 rounded-[5px] px-5 py-2.5 text-sm font-bold text-primary ring-1 ring-primary transition-colors duration-150 ring-inset hover:bg-surface-violet disabled:opacity-60"
            :disabled="submitting"
            data-test="design-system-invitation-revert"
            @click="revert"
          >
            {{ $t('appearance.switched.revert') }}
          </button>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script setup>
import { useRoute } from '@/composables/route'
import { router, usePage } from '@inertiajs/vue3'
import Vapor from 'laravel-vapor'
import { computed, ref } from 'vue'

const dismissed = ref(false)
const submitting = ref(false)

/**
 * The server decides whether there is anything to invite anyone to: the company has to have the
 * design system, the user has to predate `design-system.default_from`, and they must not have
 * answered or snoozed already (DesignSystemPreference::shouldInvite). `dismissed` only hides the
 * card for the rest of this page, before the request that makes it stick has come back.
 */
const justSwitched = computed(
  () => usePage().props._designSystemJustSwitched === true,
)

const visible = computed(
  () =>
    !dismissed.value &&
    (usePage().props._designSystemInvitation === true || justSwitched.value),
)

/**
 * Same card, two moments: an invitation before the switch, a confirmation with the way back right
 * after it. One component so the position, the entrance and the accessible name stay in one place.
 */
const copy = computed(() =>
  justSwitched.value
    ? { title: 'appearance.switched.title', body: 'appearance.switched.body' }
    : {
        title: 'appearance.invitation.title',
        body: 'appearance.invitation.body',
      },
)

/**
 * No reload here: the controller answers with `Inertia::location`, so the client makes a full page
 * visit by itself. That is what re-renders Blade's `data-ds-theme` - an ordinary Inertia visit
 * never does, and would leave one look's CSS tokens under the other look's components.
 */
const activate = () => {
  submitting.value = true

  router.put(
    useRoute('account.appearance.update'),
    { design_system: true },
    {
      onError: () => (submitting.value = false),
    },
  )
}

const revert = () => {
  submitting.value = true

  router.put(
    useRoute('account.appearance.update'),
    { design_system: false },
    {
      onError: () => (submitting.value = false),
    },
  )
}

const snooze = () => {
  dismissed.value = true

  // Nothing to snooze after a switch - that card is already a one-render thing, its cookie
  // consumed on read - so closing it is purely local.
  if (justSwitched.value) {
    return
  }

  submitting.value = true

  router.put(
    useRoute('account.appearance.invitation.snooze'),
    {},
    {
      preserveScroll: true,
      preserveState: true,
      onFinish: () => (submitting.value = false),
    },
  )
}
</script>
